// Batch E2.4 — Dashboard widget: 'Pipeline at a Glance'.
//
// Renders all 8 stages as colored mini-pills with their counts. Each pill
// links to /portfolio?stage=<id> so a single click filters the Portfolio
// list to that bucket.
//
// Data is sourced from usePipelineCounts() — shared with StageTabs so the
// network fires once across both surfaces and stays in sync with badge
// PATCH mutations via invalidatePipelineCounts().

import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { useStages } from "@/components/pipeline/StageBadge";
import usePipelineCounts from "@/lib/usePipelineCounts";

export default function PipelineGlanceCard() {
  const stages = useStages();
  const { counts, total, loading, error, refetch } = usePipelineCounts();

  // Phase 45 Batch — Dashboard P0 fix. Hard watchdog so the widget
  // NEVER renders "Loading…" forever, even if the upstream hook's
  // own timeout misbehaves. After 12 seconds of continuous loading
  // we surface a "Pipeline syncing…" + Retry control so the user
  // can recover with a single tap.
  const [watchdogTriggered, setWatchdogTriggered] = useState(false);
  useEffect(() => {
    if (!loading) {
      setWatchdogTriggered(false);
      return undefined;
    }
    const id = setTimeout(() => setWatchdogTriggered(true), 12_000);
    return () => clearTimeout(id);
  }, [loading]);

  // Phase 36 — Dashboard P0 follow-up. Choose a single status line so we
  // NEVER show "Loading…" forever. Four terminal states:
  //   • watchdog → "Pipeline syncing… try retry"   (loading too long)
  //   • loading  → "Loading…"
  //   • error    → short retry prompt
  //   • zero     → "No deals in pipeline yet"      (Phase 45)
  //   • success  → "{N} deals across 8 stages"
  let statusLine;
  let showRetry = false;
  if (watchdogTriggered && loading) {
    statusLine = "Pipeline syncing… tap retry if this persists.";
    showRetry = true;
  } else if (loading) {
    statusLine = "Loading…";
  } else if (error) {
    statusLine =
      error === "unauthorized"
        ? "Sign in again to load pipeline."
        : error === "timeout"
          ? "Pipeline took too long. Tap retry."
          : "Couldn't load pipeline.";
    showRetry = true;
  } else if ((total ?? 0) === 0) {
    statusLine = "No deals in pipeline yet — add one to populate stage buckets.";
  } else {
    const n = total ?? 0;
    statusLine = `${n} deal${n === 1 ? "" : "s"} across 8 stages`;
  }

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="pipeline-glance-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Pipeline at a Glance
          </p>
          <p
            className="mt-0.5 text-sm text-slate-700"
            data-testid="pipeline-glance-status"
          >
            {statusLine}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {showRetry ? (
            <button
              type="button"
              onClick={() => refetch()}
              data-testid="pipeline-glance-retry"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="h-3 w-3" /> Retry
            </button>
          ) : null}
          <Link
            to="/portfolio"
            data-testid="pipeline-glance-view-all"
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            View portfolio →
          </Link>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="pipeline-glance-pills">
        {stages.map((s) => {
          // When the fetch errored we still render the pills with "—" so
          // the user sees the 8-stage layout. On success, "0" renders as
          // "0" (not "—") so empty buckets read correctly.
          const n = counts ? (counts[s.id] ?? 0) : null;
          return (
            <Link
              key={s.id}
              to={`/portfolio?stage=${s.id}`}
              data-testid={`pipeline-glance-pill-${s.id}`}
              className={`group inline-flex items-center gap-1.5 rounded-full ${s.colors.bg} ${s.colors.text} px-3 py-1 text-xs font-medium transition-all hover:ring-2 hover:ring-indigo-200 active:scale-[0.97]`}
            >
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 rounded-full ${s.colors.bg.replace("-100", "-500")}`}
              />
              <span>{s.label}</span>
              <span className="font-semibold opacity-90">
                {n == null ? "—" : n}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
